"use client"

import { Images } from "@/constants/images"
import { buildFacebookShareUrl, buildTwitterShareUrl, openShareWindow } from "@/constants/social-share"
import { getFixedT } from "@/languages/i18n"
import { formatDateSimple, toImageSrc } from "@/lib/utils"
import { Input } from "antd"
import Image from "next/image"

interface LeafletShareProps {
    item: any
    locale: string
    onCopyLink: () => void
    variant?: "modal" | "panel" // modal: popup với backdrop, panel: fixed panel slide in
    show?: boolean
    onClose?: () => void
    id?: number
}

export const LeafletShare = ({
    item,
    locale,
    onCopyLink,
    variant = "modal",
    show = true,
    onClose,
    id
}: LeafletShareProps) => {
    const t = getFixedT(locale)
    const handleShareFacebook = () => {
        const url = `${window.location.origin}/${locale}/letak/${id}`
        const shareUrl = buildFacebookShareUrl(url)
        openShareWindow(shareUrl)
    }

    const handleShareTwitter = () => {
        const url = `${window.location.origin}/${locale}/letak/${id}`
        const shareUrl = buildTwitterShareUrl(url)
        openShareWindow(shareUrl)
    }

    const isPanel = variant === "panel"

    if (isPanel) {
        return (
            <div className={`fixed sm:block hidden left-[3.2rem] top-0 h-screen w-1/4 bg-white z-[10000] overflow-y-auto origin-left transition-transform duration-300 ${show ? 'scale-x-100' : 'scale-x-0'}`}>
                <div className="p-4 flex flex-col gap-8 ">
                    <h2 className="text-2xl font-bold leading-[37.2px] text-[#333]">{t("Share event")}</h2>

                    <div className="flex gap-4 items-center">
                        {item?.attributes?.featuredImage && (
                            <div className="relative w-[75px] h-[75px]">
                                <Image
                                    src={toImageSrc(item?.attributes?.featuredImage?.data?.attributes?.url)}
                                    alt={item?.attributes?.title}
                                    fill
                                    className="object-cover w-full h-full"
                                />
                            </div>
                        )}
                        <div className="flex-1">
                            <h3 className="text-base leading-[24.8px] font-semibold text-[#333]">{item?.attributes?.title}</h3>
                            {item?.attributes?.dateStart && item?.attributes?.dateEnd && (
                                <p className="text-xs leading-[18.6px] font-normal text-[#333]">
                                    {formatDateSimple(item?.attributes?.dateStart)} - {formatDateSimple(item?.attributes?.dateEnd)}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex flex-col gap-8">
                        <button
                            onClick={handleShareFacebook}
                            className="flex items-center gap-4 px-4 text-base leading-[24.8px] text-[#333]"
                        >
                            <Image src={Images.facebookIcon} alt="Facebook" width={31} height={31} />
                            {t("Facebook")}
                        </button>
                        <button
                            onClick={handleShareTwitter}
                            className="flex items-center gap-4 px-4 text-base leading-[24.8px] text-[#333]"
                        >
                            <Image src={Images.twitterIcon} alt="Twitter" width={31} height={31} />
                            {t("Twitter")}
                        </button>
                    </div>

                    <div className="border-t border-gray-200"></div>

                    <div className="flex flex-col gap-4">
                        <h4 className="text-sm font-medium text-gray-700">{t("Copy link to share")}</h4>
                        <Input
                            type="text"
                            readOnly
                            value={`${typeof window !== 'undefined' ? window.location.origin : ''}/${locale}/letak/${id}`}
                            className="flex-1 p-4 border border-black rounded-none focus:outline-none focus:ring-2 focus:ring-[#00A651] text-base leading-[24.8px]"
                        />
                    </div>
                    <div className="flex justify-end">
                        <button
                            onClick={onCopyLink}
                            className="px-[42px] bg-[#00A651] text-white rounded-lg hover:bg-[#008a43] transition-colors text-base leading-9 font-semibold"
                        >
                            {t("Copy")}
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    // Modal variant
    if (!show) return null

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full p-6 relative">
                {onClose && (
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}

                <h2 className="text-2xl font-bold text-gray-800 mb-6">{t("Share event")}</h2>

                <div className="flex gap-4 mb-6">
                    {item?.attributes?.featuredImage && (
                        <div className="relative w-32 h-32 flex-shrink-0 rounded-lg overflow-hidden border">
                            <Image
                                src={item?.attributes?.featuredImage?.data?.attributes?.url}
                                alt={item?.attributes?.title}
                                fill
                                className="object-cover"
                            />
                        </div>
                    )}
                    <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">{item?.attributes?.title}</h3>
                        <p className="text-sm text-gray-600">
                            {item.status === 'ongoing' && t("Ongoing")}
                            {item.status === 'upcoming' && t("Upcoming")}
                            {item.status === 'expired' && t("Expired")}
                        </p>
                    </div>
                </div>

                <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">{t("Share on social media")}</h4>
                    <div className="flex gap-4">
                        <button
                            onClick={handleShareFacebook}
                            className="flex items-center gap-2 px-4 py-2 bg-[#1877F2] text-white rounded-lg hover:bg-[#166FE5] transition-colors"
                        >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                            </svg>
                            <span>{t("Facebook")}</span>
                        </button>
                        <button
                            onClick={handleShareTwitter}
                            className="flex items-center gap-2 px-4 py-2 bg-[#1DA1F2] text-white rounded-lg hover:bg-[#1a8cd8] transition-colors"
                        >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                            </svg>
                            <span>{t("Twitter")}</span>
                        </button>
                    </div>
                </div>

                <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">{t("Copy link to share")}</h4>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            readOnly
                            value={`${typeof window !== 'undefined' ? window.location.origin : ''}/${locale}/letak/${id}`}
                            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00A651]"
                        />
                        <button
                            onClick={onCopyLink}
                            className="px-6 py-2 bg-[#00A651] text-white rounded-lg hover:bg-[#008a43] transition-colors"
                        >
                            {t("Copy")}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

